import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { api } from '../services/api/client.js';

/**
 * GenerationStatusContext — single source of truth for in-flight async
 * operations across the app. Covers content generation batches, single
 * drafts, plan regenerations, visual generation, carousel generation,
 * social account connects, and publish-now operations.
 *
 * Lives ABOVE per-screen state so a user can:
 *   - Start a publish-now from EditPost, navigate to Library, and still
 *     see the operation progress in the global plate.
 *   - Hard-refresh the browser mid-op and see the same operations
 *     restored from sessionStorage (server reconciliation pending).
 *   - Get a single toast/banner pattern for every async op instead of
 *     per-screen bespoke loaders.
 *
 * Operation shape:
 *   {
 *     id: string,                  // client-generated unique id
 *     kind: OperationKind,         // see OPERATION_KINDS
 *     label: string,               // user-facing one-liner, e.g. "Publishing to Facebook + Instagram"
 *     subtype: string?,            // optional refinement, e.g. 'publish_now' for kind='publish'
 *     sourceRef: {                 // pointer to the canonical backend resource
 *       type: 'batch'|'scheduledPost'|'job'|'socialAccount'|'contentItem',
 *       id: string,
 *     },
 *     deeplink: string?,           // where the "View" / "Open" CTA navigates
 *     status: 'pending'|'running'|'succeeded'|'failed'|'cancelled',
 *     progress: number,            // 0..total (or 0..100 if total is null)
 *     total: number?,              // total work units, null when indeterminate
 *     completed: number,           // sub-units done (e.g. published targets)
 *     failed: number,              // sub-units failed
 *     startedAt: number,           // Date.now()
 *     updatedAt: number,           // Date.now()
 *     expectedDurationMs: number?, // optional ETA hint
 *     result: any?,                // terminal payload (e.g. published URL)
 *     error: string?,              // terminal error message
 *     dismissed: boolean,          // user closed the row from the plate
 *     platforms: string[]?,        // optional per-platform breakdown
 *     platformStatus: Record<string, 'pending'|'running'|'succeeded'|'failed'>?,
 *   }
 */

const STORAGE_KEY = 'dathent.generation-status.v1';
const RECENT_COMPLETION_WINDOW_MS = 8000;   // succeeded/failed stays visible 8s
const AUTO_DISMISS_AFTER_MS = 60_000;        // failed ops auto-dismiss after 60s
const POLL_INTERVAL_FAST_MS = 4000;
const POLL_INTERVAL_SLOW_MS = 10_000;
const POLL_SLOW_AFTER_TICKS = 30;

export const OPERATION_KINDS = Object.freeze({
  CONTENT_BATCH: 'content_batch',
  SINGLE_DRAFT: 'single_draft',
  PUBLISH_NOW: 'publish',
  PLAN_REGENERATE: 'plan_regenerate',
  VISUAL: 'visual',
  CAROUSEL: 'carousel',
  SOCIAL_CONNECT: 'social_connect',
  BRANDKIT_IMAGE: 'brandkit_image',
  // Reel render (multi-minute, dispatch→json2video→webhook). Value matches the
  // backend async-op kind 'video_reel' so a render survives a page reload and
  // shows in the global Activity feed like any other op.
  VIDEO_RENDER: 'video_reel',
});

const initialState = {
  operations: {},   // id -> operation
  collapsed: false, // user-toggled plate collapse
  hydrated: false,  // sessionStorage hydration complete
};

function reducer(state, action) {
  switch (action.type) {
    case 'HYDRATE': {
      return { ...state, ...action.payload, hydrated: true };
    }
    case 'START': {
      const op = action.payload;
      return {
        ...state,
        operations: {
          ...state.operations,
          [op.id]: { ...op, dismissed: false, updatedAt: Date.now() },
        },
      };
    }
    case 'UPDATE': {
      const { id, patch } = action.payload;
      const existing = state.operations[id];
      if (!existing) return state;
      // Don't downgrade a terminal status back to running.
      if (isTerminal(existing.status) && patch.status && !isTerminal(patch.status)) {
        return state;
      }
      return {
        ...state,
        operations: {
          ...state.operations,
          [id]: { ...existing, ...patch, updatedAt: Date.now() },
        },
      };
    }
    case 'COMPLETE': {
      const { id, status, result, error, errorCode, completed, failed, platformStatus } = action.payload;
      const existing = state.operations[id];
      if (!existing) return state;
      return {
        ...state,
        operations: {
          ...state.operations,
          [id]: {
            ...existing,
            status,
            result: result ?? existing.result,
            error: error ?? existing.error,
            errorCode: errorCode ?? existing.errorCode,
            completed: completed ?? existing.completed,
            failed: failed ?? existing.failed,
            platformStatus: platformStatus ?? existing.platformStatus,
            progress: existing.total ?? (status === 'succeeded' ? 100 : existing.progress),
            updatedAt: Date.now(),
            completedAt: Date.now(),
          },
        },
      };
    }
    case 'DISMISS': {
      const existing = state.operations[action.payload];
      if (!existing) return state;
      return {
        ...state,
        operations: {
          ...state.operations,
          [action.payload]: { ...existing, dismissed: true, updatedAt: Date.now() },
        },
      };
    }
    case 'PURGE': {
      // Drop dismissed terminal ops or terminal ops older than the auto-dismiss window.
      const now = Date.now();
      const next = {};
      Object.values(state.operations).forEach((op) => {
        if (op.dismissed && isTerminal(op.status)) return;
        if (isTerminal(op.status) && now - (op.completedAt ?? op.updatedAt) > AUTO_DISMISS_AFTER_MS) return;
        next[op.id] = op;
      });
      return { ...state, operations: next };
    }
    case 'SET_COLLAPSED': {
      return { ...state, collapsed: Boolean(action.payload) };
    }
    case 'RESET': {
      return { ...initialState, hydrated: true };
    }
    case 'SYNC_SERVER': {
      const serverOps = Array.isArray(action.payload) ? action.payload : [];
      const serverIds = new Set(serverOps.map((s) => s.id));
      const next = { ...state.operations };
      // In-session dedupe: a running CLIENT op (e.g. EditPost's regen) already
      // represents its content, so skip the server row for the same content
      // while that client op is alive. After a reload the client op is gone and
      // the server row takes over (that's what makes it survive reloads).
      const clientCovered = new Set();
      Object.values(state.operations).forEach((op) => {
        if (!op.serverTracked && !isTerminal(op.status) && !op.dismissed && op.sourceRef?.id) {
          clientCovered.add(op.sourceRef.id);
        }
      });
      serverOps.forEach((s) => {
        const batchId = s.batchId || s.batch_id;
        if (
          s.status === 'running' &&
          (clientCovered.has(s.contentItemId || s.content_item_id) || clientCovered.has(batchId))
        ) return;
        const existing = next[s.id];
        if (existing?.dismissed) return; // don't resurrect a dismissed row
        const mapped = mapServerOp(s);
        // Terminal-downgrade guard: once the client has surfaced a terminal
        // status for an op, don't let a later server row flip it to a DIFFERENT
        // status (e.g. a self-healed 'succeeded' being silently reverted to
        // 'failed' after the completion effect already consumed the id). Only
        // relevant across a terminal→terminal change; running→terminal is fine.
        if (existing && isTerminal(existing.status) && existing.status !== mapped.status) return;
        // Don't re-hydrate an aged-out terminal row the client already purged:
        // with RECENT_DONE_MS=5min the server keeps returning a done op long
        // after the client's AUTO_DISMISS purge, which would otherwise re-add it
        // (invisible, but wasteful) every poll. If it's terminal, already past
        // the dismiss window, and not currently in state, skip it.
        if (
          !existing &&
          isTerminal(mapped.status) &&
          mapped.completedAt &&
          Date.now() - mapped.completedAt > AUTO_DISMISS_AFTER_MS
        ) {
          return;
        }
        next[s.id] = { ...(existing || {}), ...mapped };
      });
      // A server-tracked running op no longer returned = finished/stale on the
      // server → reconcile so it can't hang as a zombie.
      Object.values(next).forEach((op) => {
        if (op.serverTracked && !isTerminal(op.status) && !serverIds.has(op.id)) {
          // VIDEO_RENDER self-heal. A still-rendering reel stays in the feed
          // until STALE_RUNNING_MS (20min, > max render + the 15min reaper) and a
          // completed one lingers RECENT_DONE_MS (5min), so by the time a reel
          // op VANISHES it has finished and aged out — and any real FAILURE
          // would have been returned as 'failed' first. So terminalize as
          // succeeded (not just dismissed) → the composer's completion effect
          // fires and refetches the finished video, instead of hanging on
          // "Generating…" forever (owner report 2026-07-09). Residual edge: a
          // tab hidden for the whole render+done window could miss a failure and
          // show a false 'ready' — bounded, and strictly better than the hang.
          // Other kinds keep dismiss-only — we can't assume their outcome.
          if (op.kind === OPERATION_KINDS.VIDEO_RENDER) {
            next[op.id] = { ...op, status: 'succeeded', completedAt: op.completedAt ?? Date.now() };
          } else {
            next[op.id] = { ...op, dismissed: true };
          }
        }
      });
      return { ...state, operations: next };
    }
    default:
      return state;
  }
}

function isTerminal(status) {
  return status === 'succeeded' || status === 'failed' || status === 'cancelled';
}

// Map a server async_operations row to the in-memory operation shape used by
// the Activity feed. Server-tracked ops are the source of truth for work that
// must be visible from anywhere / across reloads (regen now, auto-gen later).
function mapServerOp(s) {
  const kindMap = {
    image_regen: OPERATION_KINDS.VISUAL,
    content_batch: OPERATION_KINDS.CONTENT_BATCH,
    brandkit_image: OPERATION_KINDS.BRANDKIT_IMAGE,
    plan_regenerate: OPERATION_KINDS.PLAN_REGENERATE,
    video_reel: OPERATION_KINDS.VIDEO_RENDER,
  };
  const kind = kindMap[s.kind] || OPERATION_KINDS.SINGLE_DRAFT;
  const total = s.total ?? null;
  const batchId = s.batchId || s.batch_id;
  const contentItemId = s.contentItemId || s.content_item_id;
  const updatedAt = Date.parse(s.updatedAt || s.updated_at) || Date.now();
  const fallbackLabel = kind === OPERATION_KINDS.CONTENT_BATCH
    ? (total > 1 ? `Generating ${total} posts` : 'Generating post')
    : 'Working…';
  return {
    id: s.id,
    kind,
    label: s.label || fallbackLabel,
    status: s.status,
    total,
    completed: s.completed ?? 0,
    progress: s.completed ?? 0,
    failed: 0,
    deeplink: s.deeplink || null,
    error: s.errorMessage || s.error_message || null,
    sourceRef: batchId
      ? { type: 'batch', id: batchId }
      : (contentItemId ? { type: 'contentItem', id: contentItemId } : null),
    startedAt: updatedAt,
    updatedAt,
    completedAt: s.completedAt || s.completed_at
      ? (Date.parse(s.completedAt || s.completed_at) || Date.now())
      : undefined,
    dismissed: false,
    serverTracked: true,
  };
}

const GenerationStatusContext = createContext(null);

export function GenerationStatusProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const pollersRef = useRef(new Map()); // id -> { tickCount, lastProgress }

  // ----- Persistence -----
  // Hydrate once on mount. Persist on every state change.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          // Drop heavy `result` payloads from persisted state — they can be re-fetched.
          // Also drop ops that look stale (>1h since update).
          const operations = {};
          const cutoff = Date.now() - 60 * 60 * 1000;
          Object.entries(parsed.operations || {}).forEach(([id, op]) => {
            if (!op || typeof op !== 'object') return;
            if ((op.updatedAt || 0) < cutoff) return;
            // Non-terminal ops are driven by an in-memory request/poller that
            // does NOT survive a full page reload — e.g. the synchronous
            // image-regen request resolves into the OLD page's context, so the
            // reloaded page never sees its completion. Re-hydrating such an op
            // as "running" produces a zombie that hangs forever. Drop them:
            // the work either already finished server-side (the item refreshes
            // on load) or is tracked elsewhere (batches via the Batch screen).
            // Only terminal ops are safe to restore (for the brief done-toast).
            if (!isTerminal(op.status)) return;
            operations[id] = { ...op, result: null };
          });
          dispatch({ type: 'HYDRATE', payload: { operations, collapsed: Boolean(parsed.collapsed) } });
          return;
        }
      }
    } catch (_) {
      // sessionStorage may be unavailable (private mode, server-render). No-op.
    }
    dispatch({ type: 'HYDRATE', payload: {} });
  }, []);

  useEffect(() => {
    if (!state.hydrated) return;
    try {
      const payload = {
        operations: Object.fromEntries(
          Object.entries(state.operations).map(([id, op]) => [id, { ...op, result: null }])
        ),
        collapsed: state.collapsed,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (_) {
      // ignored
    }
  }, [state.operations, state.collapsed, state.hydrated]);

  // ----- Periodic purge of stale terminal ops -----
  useEffect(() => {
    const interval = setInterval(() => dispatch({ type: 'PURGE' }), 5000);
    return () => clearInterval(interval);
  }, []);

  // ----- Server-backed activity sync -----
  // Poll the server for in-progress operations so the Activity feed shows work
  // happening anywhere (image regen now, auto-generated post sets later),
  // regardless of when the user opened the app or how many times they reloaded.
  // This is what makes the feed survive a refresh: the server is the source of
  // truth, the client just reflects it.
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        timer = setTimeout(poll, 8000);
        return;
      }
      try {
        const res = await api.content.activeOperations();
        const ops = res?.data?.operations || res?.operations || [];
        if (!cancelled) dispatch({ type: 'SYNC_SERVER', payload: ops });
      } catch (_) {
        // transient (offline, 5xx) — retry next tick
      }
      if (!cancelled) timer = setTimeout(poll, 8000);
    };
    timer = setTimeout(poll, 1500);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  // ----- Action creators -----
  const startOperation = useCallback((op) => {
    const id = op.id || `op_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    const enriched = {
      id,
      status: 'pending',
      progress: 0,
      total: null,
      completed: 0,
      failed: 0,
      startedAt: Date.now(),
      platforms: null,
      platformStatus: null,
      ...op,
      sourceRef: op.sourceRef || null,
    };
    dispatch({ type: 'START', payload: enriched });
    return id;
  }, []);

  const updateOperation = useCallback((id, patch) => {
    dispatch({ type: 'UPDATE', payload: { id, patch } });
  }, []);

  const completeOperation = useCallback((id, finalState = {}) => {
    dispatch({
      type: 'COMPLETE',
      payload: {
        id,
        status: finalState.status || 'succeeded',
        result: finalState.result ?? null,
        error: finalState.error ?? null,
        errorCode: finalState.errorCode ?? null,
        completed: finalState.completed,
        failed: finalState.failed,
        platformStatus: finalState.platformStatus,
      },
    });
  }, []);

  const failOperation = useCallback((id, error) => {
    completeOperation(id, { status: 'failed', error: typeof error === 'string' ? error : (error?.message || 'Operation failed') });
  }, [completeOperation]);

  const dismissOperation = useCallback((id) => {
    dispatch({ type: 'DISMISS', payload: id });
  }, []);

  const setCollapsed = useCallback((value) => {
    dispatch({ type: 'SET_COLLAPSED', payload: value });
  }, []);

  /**
   * Find an operation by its sourceRef. Used by bridges (e.g. ContentFeed
   * watcher converts a scheduled_post transition to publish-op completion).
   */
  const findBySourceRef = useCallback((type, id) => {
    if (!type || !id) return null;
    return Object.values(state.operations).find((op) =>
      op.sourceRef?.type === type && op.sourceRef?.id === id
    ) || null;
  }, [state.operations]);

  // ----- Selectors -----
  const operationsArr = useMemo(() => Object.values(state.operations).sort((a, b) => b.startedAt - a.startedAt), [state.operations]);
  const activeOperations = useMemo(() => operationsArr.filter((op) => !isTerminal(op.status) && !op.dismissed), [operationsArr]);
  const recentlyCompleted = useMemo(() => {
    const now = Date.now();
    return operationsArr.filter((op) => isTerminal(op.status) && !op.dismissed && now - (op.completedAt ?? op.updatedAt) < RECENT_COMPLETION_WINDOW_MS);
  }, [operationsArr]);
  const visibleOperations = useMemo(() => [...activeOperations, ...recentlyCompleted], [activeOperations, recentlyCompleted]);
  const hasActive = activeOperations.length > 0;

  // ----- Polling state machine (visibility-aware) -----
  // Polling itself is the responsibility of the trigger site (it knows what
  // API to call). This context just owns the cadence + visibility gating.
  // Trigger sites register pollers via `registerPoller(id, () => Promise)`.
  const registerPoller = useCallback((id, pollerFn) => {
    pollersRef.current.set(id, { fn: pollerFn, tickCount: 0, lastProgressSig: '' });
    return () => pollersRef.current.delete(id);
  }, []);

  useEffect(() => {
    if (!hasActive) return undefined;
    let cancelled = false;
    let timerId = null;

    const tick = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        timerId = setTimeout(tick, POLL_INTERVAL_FAST_MS);
        return;
      }
      const pollers = pollersRef.current;
      for (const op of activeOperations) {
        const entry = pollers.get(op.id);
        if (!entry) continue;
        const progressSig = `${op.completed}|${op.failed}|${op.progress}|${op.status}`;
        if (progressSig === entry.lastProgressSig) {
          entry.tickCount += 1;
        } else {
          entry.tickCount = 0;
          entry.lastProgressSig = progressSig;
        }
        try {
          await entry.fn(op);
        } catch (_) {
          // poller swallows its own errors; context doesn't kill the loop
        }
      }
      if (cancelled) return;
      // If every active poller has been stagnant for POLL_SLOW_AFTER_TICKS, slow down.
      const slow = activeOperations.every((op) => (pollers.get(op.id)?.tickCount ?? 0) >= POLL_SLOW_AFTER_TICKS);
      timerId = setTimeout(tick, slow ? POLL_INTERVAL_SLOW_MS : POLL_INTERVAL_FAST_MS);
    };

    timerId = setTimeout(tick, POLL_INTERVAL_FAST_MS);
    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [hasActive, activeOperations]);

  const value = useMemo(() => ({
    operations: state.operations,
    operationsArr,
    activeOperations,
    recentlyCompleted,
    visibleOperations,
    hasActive,
    collapsed: state.collapsed,
    hydrated: state.hydrated,
    startOperation,
    updateOperation,
    completeOperation,
    failOperation,
    dismissOperation,
    setCollapsed,
    findBySourceRef,
    registerPoller,
  }), [
    state.operations, state.collapsed, state.hydrated,
    operationsArr, activeOperations, recentlyCompleted, visibleOperations, hasActive,
    startOperation, updateOperation, completeOperation, failOperation, dismissOperation,
    setCollapsed, findBySourceRef, registerPoller,
  ]);

  return <GenerationStatusContext.Provider value={value}>{children}</GenerationStatusContext.Provider>;
}

export function useGenerationStatus() {
  const ctx = useContext(GenerationStatusContext);
  if (!ctx) {
    // Render-safe fallback so screens can import the hook without crashing
    // if the provider isn't mounted (e.g. legacy MainApp screens outside
    // the new AppShell tree). All actions become no-ops.
    return {
      operations: {},
      operationsArr: [],
      activeOperations: [],
      recentlyCompleted: [],
      visibleOperations: [],
      hasActive: false,
      collapsed: false,
      hydrated: true,
      startOperation: () => null,
      updateOperation: () => {},
      completeOperation: () => {},
      failOperation: () => {},
      dismissOperation: () => {},
      setCollapsed: () => {},
      findBySourceRef: () => null,
      registerPoller: () => () => {},
    };
  }
  return ctx;
}
